import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import {
    MapPin, Phone, Clock, Building2, ArrowLeft,
    Car, AlertCircle
} from 'lucide-react';

/**
 * 치과 상세 프로필 (공개)
 * - 의료법 광고 심의 불필요 콘텐츠만 노출:
 *   1) 사실 정보 (이름·주소·전화·진료시간·주차)
 *   2) 진료 항목 (HIRA 분류 기반 사실 정보)
 * - 비교광고·효과 약속·환자 치료 후기 문구는 노출하지 않음
 */
function ClinicProfile() {
    const { hospitalId } = useParams();
    const navigate = useNavigate();
    const [hospital, setHospital] = useState(null);
    // 홍보 데이터는 hospital 객체에 통합 — 별도 ad 상태 제거
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const hSnap = await getDoc(doc(db, 'hospitals', hospitalId));
                if (hSnap.exists()) {
                    setHospital({ id: hSnap.id, ...hSnap.data() });
                }
                // 홍보 데이터는 구 환자앱 홍보 페이지가 hospitals/{id} 에 저장했던 필드(해당 화면 09-15 삭제)
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        if (hospitalId) load();
    }, [hospitalId]);

    // 전화 = 기기 전화 연결만 (통화 추적 trackPatientCall 호출 삭제 — 대표 결정 2026-09-16)
    const callPhone = (phone) => {
        if (!phone) return;
        window.location.href = `tel:${phone}`;
    };

    if (loading) {
        return <div style={S.center}><div style={S.spinner} /></div>;
    }
    if (!hospital) {
        return (
            <div style={S.container}>
                <div style={S.errorBox}>
                    <AlertCircle size={20} /> 치과를 찾을 수 없습니다
                </div>
            </div>
        );
    }

    // 구독 여부 강조·PRO 배지·예약 배지 제거 (의료법 정리 2026-09-15) — 모든 치과 동일 표시
    return (
        <div style={S.container}>
            {/* 뒤로 */}
            <button onClick={() => navigate(-1)} style={S.backBtn}>
                <ArrowLeft size={16} /> 뒤로가기
            </button>

            {/* 헤더 */}
            <div style={S.hero}>
                <div style={S.heroIconWrap}>
                    <Building2 size={28} color="#10b981" />
                </div>
                <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                        <h1 style={S.title}>{hospital.name || '치과'}</h1>
                    </div>
                    {hospital.classification && (
                        <p style={S.subtitle}>{hospital.classification}</p>
                    )}
                </div>
            </div>

            {/* 인사말 (치과 작성) */}
            {hospital.description && (
                <div style={S.card}>
                    <h3 style={S.cardTitle}>인사말</h3>
                    <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap' }}>{hospital.description}</p>
                </div>
            )}

            {/* 사진 */}
            {Array.isArray(hospital.photos) && hospital.photos.length > 0 && (
                <div style={S.card}>
                    <h3 style={S.cardTitle}>치과 사진</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
                        {hospital.photos.map((url, i) => (
                            <img key={i} src={url} alt="" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }} />
                        ))}
                    </div>
                </div>
            )}

            {/* 기본 정보 (사실 정보만) */}
            <div style={S.card}>
                <h3 style={S.cardTitle}>기본 정보</h3>
                <div style={S.infoGrid}>
                    {hospital.address && (
                        <div style={S.infoRow}>
                            <MapPin size={16} color="#64748b" />
                            <div>
                                <div style={S.infoLabel}>주소</div>
                                <div style={S.infoValue}>{hospital.address}</div>
                            </div>
                        </div>
                    )}
                    {hospital.phone && (
                        <div style={S.infoRow}>
                            <Phone size={16} color="#64748b" />
                            <div style={{ flex: 1 }}>
                                <div style={S.infoLabel}>전화</div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                    <span style={S.infoValue}>{hospital.phone}</span>
                                    <button onClick={() => callPhone(hospital.phone)} style={S.callBtn}>
                                        <Phone size={12} /> 전화걸기
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {hospital.openHours && Object.values(hospital.openHours).some(v => v) && (
                        <div style={S.infoRow}>
                            <Clock size={16} color="#64748b" />
                            <div>
                                <div style={S.infoLabel}>진료시간</div>
                                {[
                                    { k: 'mon', l: '월' }, { k: 'tue', l: '화' }, { k: 'wed', l: '수' },
                                    { k: 'thu', l: '목' }, { k: 'fri', l: '금' }, { k: 'sat', l: '토' }, { k: 'sun', l: '일' },
                                ].map(d => hospital.openHours[d.k] && (
                                    <div key={d.k} style={{ fontSize: 13, color: '#475569' }}>
                                        {d.l}: {hospital.openHours[d.k]}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {hospital.parkingInfo && (
                        <div style={S.infoRow}>
                            <Car size={16} color="#64748b" />
                            <div>
                                <div style={S.infoLabel}>주차</div>
                                <div style={S.infoValue}>{hospital.parkingInfo}</div>
                            </div>
                        </div>
                    )}
                    {hospital.transportationInfo && (
                        <div style={S.infoRow}>
                            <MapPin size={16} color="#64748b" />
                            <div>
                                <div style={S.infoLabel}>대중교통</div>
                                <div style={S.infoValue}>{hospital.transportationInfo}</div>
                            </div>
                        </div>
                    )}
                </div>
                {/* 외부 링크 */}
                {(hospital.websiteUrl || hospital.instagramUrl || hospital.kakaoChannelUrl) && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
                        {hospital.websiteUrl && <a href={hospital.websiteUrl} target="_blank" rel="noreferrer" style={S.linkBtn}>🌐 홈페이지</a>}
                        {hospital.instagramUrl && <a href={hospital.instagramUrl} target="_blank" rel="noreferrer" style={S.linkBtn}>📷 인스타그램</a>}
                        {hospital.kakaoChannelUrl && <a href={hospital.kakaoChannelUrl} target="_blank" rel="noreferrer" style={S.linkBtn}>💬 카카오 채널</a>}
                    </div>
                )}
            </div>

            {/* 보유 장비 (치과 작성) */}
            {Array.isArray(hospital.equipment) && hospital.equipment.length > 0 && (
                <div style={S.card}>
                    <h3 style={S.cardTitle}>보유 장비</h3>
                    <div style={S.tagWrap}>
                        {hospital.equipment.map((s, i) => (
                            <span key={i} style={{ ...S.subjectTag, background: '#f0fdf4', color: '#16a34a' }}>{s}</span>
                        ))}
                    </div>
                </div>
            )}

            {/* 진료 항목 (HIRA 분류 — 사실 정보) */}
            {Array.isArray(hospital.subjects) && hospital.subjects.length > 0 && (
                <div style={S.card}>
                    <h3 style={S.cardTitle}>진료 항목</h3>
                    <div style={S.tagWrap}>
                        {hospital.subjects.map((s, i) => (
                            <span key={i} style={S.subjectTag}>{s}</span>
                        ))}
                    </div>
                    <p style={S.notice}>※ 건강보험심사평가원(HIRA) 등록 정보 기준</p>
                </div>
            )}

            {/* 환자 태그 리뷰 UI 제거 09-15 — 구 환자앱 리뷰(clinicReviews) 폐기, 의료법상 리뷰 노출 금지 방향 */}

            {/* 안내 (의료법 컴플라이언스) */}
            <div style={S.complianceBox}>
                <AlertCircle size={14} />
                <span>
                    이 페이지는 의료법 시행령 제24조에 따라 사실에 근거한 정보만 제공합니다.
                    실제 진료 가능 여부·요금·예약은 치과로 직접 문의해주세요.
                </span>
            </div>
        </div>
    );
}

const S = {
    container: { padding: 20, maxWidth: 760, margin: '0 auto', fontFamily: "'Apple SD Gothic Neo', sans-serif" },
    backBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, color: '#475569', fontWeight: 700, cursor: 'pointer', marginBottom: 14 },
    hero: { display: 'flex', alignItems: 'center', gap: 14, padding: 20, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, marginBottom: 14 },
    heroIconWrap: { width: 56, height: 56, borderRadius: 14, background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    title: { margin: 0, fontSize: 22, fontWeight: 900, color: '#0f172a' },
    subtitle: { margin: 0, fontSize: 13, color: '#64748b' },
    card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 18, marginBottom: 12 },
    cardTitle: { fontSize: 14, fontWeight: 800, color: '#0f172a', margin: '0 0 12px 0' },
    infoGrid: { display: 'flex', flexDirection: 'column', gap: 12 },
    infoRow: { display: 'flex', gap: 10, alignItems: 'flex-start', paddingBottom: 10, borderBottom: '1px solid #f1f5f9' },
    infoLabel: { fontSize: 11, color: '#94a3b8', fontWeight: 700, marginBottom: 2 },
    infoValue: { fontSize: 13, color: '#0f172a', fontWeight: 600 },
    callBtn: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' },
    tagWrap: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 },
    subjectTag: { padding: '4px 10px', background: '#eef2ff', color: '#4338ca', borderRadius: 999, fontSize: 12, fontWeight: 700 },
    linkBtn: { padding: '6px 12px', background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, fontWeight: 700, textDecoration: 'none' },
    notice: { fontSize: 11, color: '#94a3b8', marginTop: 10, marginBottom: 0, lineHeight: 1.6 },
    complianceBox: { display: 'flex', alignItems: 'flex-start', gap: 8, padding: 12, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, fontSize: 11, color: '#0c4a6e', lineHeight: 1.6, marginBottom: 12 },
    errorBox: { display: 'flex', alignItems: 'center', gap: 8, padding: 16, background: '#fee2e2', color: '#991b1b', borderRadius: 10 },
    center: { padding: 60, textAlign: 'center' },
    spinner: { width: 32, height: 32, border: '3px solid #e2e8f0', borderTop: '3px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' },
};

export default ClinicProfile;
